/* Core */
import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { finalize, tap } from "rxjs/operators";
import { Router } from "@angular/router";

/* Modelos */
import {
  MessageModel,
  ErrorMessageModel,
} from "../shared/models/message-model.model";
import { ErrorSeverityEnum } from "../shared/models/error-severity-enum.model";
import { SubCodeExceptionEnum } from "../shared/models/sub-code-exception-enum.model";

/* Services */
import { MessageService } from "../shared/messages/message.service";
import { NavigationHistoryService } from "../navigation-history.service";
import { ToastrService } from "ngx-toastr";
import { AuthenticationService } from "../core/authentication/authentication.service";

@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
  // Mensagens para envio
  message: MessageModel;
  errorMessage: ErrorMessageModel;

  constructor(
    private messenger: MessageService,
    private router: Router,
    private navigationService: NavigationHistoryService,
    private toastr: ToastrService,
    private authenticationService: AuthenticationService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const started = Date.now();
    let response: any;

    // extend server response observable with logging
    return next.handle(req).pipe(
      tap(
        // Succeeds when there is a response; ignore other events
        (event) => (response = event),
        // Operation failed; error is an HttpErrorResponse
        (error) => (response = error)
      ),
      // Log when response observable either completes or errors
      finalize(() => {
        // Se for HttpErrorResponse, adiciona a mensagem na lista de erros a tratar
        if (response instanceof HttpErrorResponse) {
          if (!navigator.onLine) {
            // Verifica se o navegador está com conexão de internet
            this.toastr.error(
              "O seu navegador está OffLine, verifique sua conexão.",
              "Erro!"
            );
          } else {
            // Se não, inclui na lista de erros a serem tratados

            // Trata caso de 401 com usuário expirado (Unauthorized)-(Expired)
            if (
              this.isUnauthorizedCode(response) &&
              this.isExpiredTokenSubCode(response)
            ) {
              this.authenticationService.resetToken();
              this.navigateToExpiredToken();
            } else if (
              this.isUnauthorizedCode(response) &&
              (this.isUnauthorizedDataAccessSubCode(response) ||
                this.isUnauthorizedActionSubCode(response))
            ) {
              // Trata caso de 401 com acesso não autorizado ou ação não autorizada (Unauthorized)-(UnauthDataAccess/UnauthAction)
              this.navigateToLastRoute();
            } else if (response.status === 401) {
              // Trata caso de 401
              this.navigateToUnauthorized();
            }

            // Monta o objeto ErrorMessage para adicionar na lista de tratamento do service
            this.errorMessage = {
              title: "Oops!",
              text: this.isThreatedErr(response)
                ? `${response.error.Message}`
                : "Ocorreu um erro ao executar a operação.",
              type: 'error',
              toast: false,
              errorStack: `"${req.method}" / "${
                req.urlWithParams
              }" / Tempo em execução: "${Date.now() - started}"`,
              errorSeverity: ErrorSeverityEnum.High,
              isServerSide: true,
              isThreatedError: this.isThreatedErr(response),
              alreadySended: false,
              showToUser: this.isExpiredTokenSubCode(response) ? false : true,
            };
            // Adiciona o erro na lista
            this.messenger.addErrorToVault(this.errorMessage);
          }
        }
      })
    );
  }

  /**
   * Método responsável por informar se o Erro é tratado ou não.
   * @param err ErrorResponse recebido pela chamada HTTP.
   */
  private isThreatedErr(err: any): boolean {
    return err.error && err.error.CustomErrorType === "ApplicationException";
  }

  /**
   * Navega para a rota de unauthorized passando o parâmetro que informa ser token expirado.
   */
  private navigateToExpiredToken(): void {
    //this.router.navigate(['unauthorized', 401]);
    this.router.navigate(["/"]);
  }

  /**
   * Navega para rota de unauthorized.
   */
  private navigateToUnauthorized(): void {
    this.router.navigate(["unauthorized"]);
  }

  /**
   * Navega para a última rota acessada.
   */
  private navigateToLastRoute(): void {
    const routeHistory = this.navigationService.getHistory();
    this.router.navigate([routeHistory[routeHistory.length - 2]]);
  }

  /**
   * Informa se o código da resposta é unauthorized.
   * @param httpResponse Objeto de response.
   */
  private isUnauthorizedCode(httpResponse: any): boolean {
    return httpResponse.status === 401;
  }

  /**
   * Informa se o sub code está presente na resposta.
   * @param httpResponse Objeto de response.
   */
  private doSubCodeExists(httpResponse: any): boolean {
    return httpResponse.error.SubCode;
  }

  /**
   * Informa se o sub code de token expirado está presente na resposta.
   * @param httpResponse Objeto de response.
   */
  private isExpiredTokenSubCode(httpResponse: any): boolean {
    return (
      this.doSubCodeExists(httpResponse) &&
      httpResponse.error.SubCode === SubCodeExceptionEnum.ExpiredToken
    );
  }

  /**
   * Informa se o sub code de acesso a dados não autorizado está presente na resposta.
   * @param httpResponse Objeto de response.
   */
  private isUnauthorizedDataAccessSubCode(httpResponse: any): boolean {
    return (
      this.doSubCodeExists(httpResponse) &&
      httpResponse.error.SubCode === SubCodeExceptionEnum.UnauthorizedDataAccess
    );
  }

  /**
   * Informa se o sub code de ação não autorizada está presente na resposta.
   * @param httpResponse Objeto de response.
   */
  private isUnauthorizedActionSubCode(httpResponse: any): boolean {
    return (
      this.doSubCodeExists(httpResponse) &&
      httpResponse.error.SubCode === SubCodeExceptionEnum.UnauthorizedAction
    );
  }
}
